import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { DEV_CREDENTIALS, FAILURE_REASON, LoginService } from '@src/app/services/login/login.service';
import { EnvironmentConfig } from '@src/app/configs/environment.config';

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  public readonly maxLength: number = 255;
  public readonly portalVersion: string;
  public waitingForResponse: boolean = false;
  public submittedAtLeastOnce: boolean = false;

  private _returnUrl: string = '/';

  public loginForm: FormGroup;

  // convenience getter for easy access to form fields
  get f(): FormGroup['controls'] {
    return this.loginForm.controls;
  }

  constructor(
    protected readonly _ENV_CONFIG: EnvironmentConfig,
    private _formBuilder: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loginService: LoginService
  ) {
    this.portalVersion = this._ENV_CONFIG.version;
    this.loginForm = this._formBuilder.group({
      email: ['', [Validators.email, Validators.maxLength(this.maxLength), Validators.required]],
      password: ['', [Validators.maxLength(this.maxLength), Validators.required]],
    });
    // redirect to home if already logged in
    if (this._loginService.isLoggedIn) {
      if (this._loginService.isMfaAuthenticated) {
        this._router.navigateByUrl('/');
      } else {
        this._router.navigateByUrl('/login/mfa');
      }
    }
  }

  /**
   * Checks to see if the state contains an e-mail and fills it in if present.
   */
  public ngOnInit(): void {
    if (history.state && history.state.email) {
      this.loginForm.setValue({
        email: history.state.email,
        password: '',
      });
    }
    // get return url from route parameters or default to '/'
    this._returnUrl = this._route.snapshot.queryParams['returnUrl'] || '/';
  }

  onSubmit(nextUrl?: string): void {
    this.f.email.setValue((this.f.email.value || '').trim());

    this.f.email.updateValueAndValidity();
    this.f.email.updateValueAndValidity(); // removes 'wrong' error from previous failed attempt

    this.submittedAtLeastOnce = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.waitingForResponse = true;

    this._loginService.login$(this.f.email.value, this.f.password.value)
      .pipe(first())
      .subscribe({
        next: () => {
          this.waitingForResponse = false;
          this.onLoginSuccess(nextUrl);
        },
        error: (err: { status: number }) => {
          const reason = LoginService.getFailureReason(err);

          if (reason === FAILURE_REASON.INVALID_RESPONSE
            || reason === FAILURE_REASON.UNKNOWN) {
            this.f.password.setErrors({ unknown: true });
          }

          if (reason === FAILURE_REASON.INVALID_CREDENTIALS) {
            this.f.password.setErrors({ wrong: true });
          }

          this.waitingForResponse = false;
        },
      });
  }

  onLoginSuccess(nextUrl?: string): void {
    this._router.navigateByUrl(nextUrl || '/login/mfa', {
      state: { returnUrl: this._returnUrl },
    });
  }

  /** DEV-only way to login without having to fill-in credentials */
  cheat(): void {
    if (this._ENV_CONFIG.production) {
      return;
    }

    this.f.email.setValue(DEV_CREDENTIALS.email);
    this.f.password.setValue(DEV_CREDENTIALS.password);
    this.onSubmit();
  }

  /**
   * Navigates to the password forgotten page and passes the email to the component.
   */
  public passwordForgotten(): void {
    this._router.navigateByUrl(
      'passwordReset',
      { state: { email: this.f.email.value } }
    );
  }
}
